import { Badge, Box, Menu, MenuItem, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";

import { ReactComponent as IconGridShapes } from "../../assets/IconGridShapes.svg";
import { ReactComponent as IconGridShapesFill } from "../../assets/IconGridShapesFill.svg";
import { ReactComponent as IconMedal } from "../../assets/IconMedal.svg";
import { ReactComponent as IconMedalFill } from "../../assets/IconMedalFill.svg";
import { ReactComponent as IconRepeat } from "../../assets/IconRepeat.svg";
import { ReactComponent as IconCheck } from "../../assets/IconCheck.svg";
import { useEffect, useRef, useState } from "react";
import { Module, Question } from "../utils/CourseTree";
import { generateGtmEvent } from "../../Helpers";
import { QuestionType } from "../../enums";
import { WrapperQuestionsDropdown } from "../v2/learning/WrapperQuestionsDropdown";
import { ColorOptions, IconButtonContained } from "../v2/utils/IconButtonContained";
import { IconButtonOutlined } from "../v2/utils/IconButtonOutlined";
import { CourseStructureCtx, UserCtx } from "../../interfaces";
import { CourseMetadata } from "@backend-types";
import { Helmet } from "react-helmet-async";

const useStyles = makeStyles((theme) => ({
  root: {
    height: 72,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxSizing: "border-box",
    borderBottom: `2px solid ${theme.palette.divider}`,
    paddingLeft: 32,
    paddingRight: 32,
    [theme.breakpoints.up("lg")]: {
      paddingLeft: 86,
      paddingRight: 86,
    },
  },
  buttonText: {
    fontSize: "1.125em",
  },
  remediationBadge: {
    "& .MuiBadge-dot": {
      height: 12,
      minWidth: 12,
      borderRadius: "50%",
      right: 6,
      top: 6,
    },
  },
  remediationBadgeMenuItem: {
    "& .MuiBadge-dot": {
      height: 12,
      minWidth: 12,
      borderRadius: "50%",
      position: "absolute",
      right: -12,
      top: 6,
    },
  },
  infoIcon: {
    color: "#0288D1",
  },
  menuPaper: {
    overflow: "visible",
    backgroundColor: "transparent",
  },
  menuList: {
    transform: "translate(0px, 8px)",
    backgroundColor: "#FFF",
    boxShadow: "0px 8px 12px 0px rgba(0, 0, 0, 0.19)",
    width: "259px",
  },
  menuWrapper: {
    fontFamily: "Roboto, sans-serif",
    padding: "0 0.938rem",
  },
  menuItem: {
    padding: "8px 12px",
  },
  menuItemIcon: {
    marginRight: "8px",

    fontSize: "1em",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "normal",
  },
  iconButton: {
    boxSizing: "border-box",
    width: "38px",
    height: "38px",
    fontSize: "1.125rem",
  },
}));

interface Lti1p3QuestionSelectorProps {
  allTargetQuestions: Question[];
  currentQuestion: Question;
  showSkillVideos: boolean;
  isSolverLoaded: boolean;
  questionFeedback?: string;
  userCtx: UserCtx;
  userCtxRef: React.MutableRefObject<UserCtx>;
  courseStructureCtx: CourseStructureCtx;
  courseStructureCtxRef: React.MutableRefObject<CourseStructureCtx>;
  handleMoveForward: (
    goToProblem: Question | null,
    suppressEoT?: boolean,
    suppressEoM?: boolean
  ) => void;
  handleNavigateAway: (callBack: () => void) => void;
  handleOpenSkillModal: () => void;
  remediationReminder: boolean;
  remediationReminderDot: boolean;
  handleHideRemediationReminder: () => void;
  metadata?: CourseMetadata;
  showGppButton?: boolean;
  currentModule?: Module;
}

export const Lti1p3QuestionSelector = (props: Lti1p3QuestionSelectorProps) => {
  const {
    allTargetQuestions,
    currentQuestion,
    handleMoveForward,
    handleNavigateAway,
    showGppButton,
    currentModule,
  } = props;
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [currentFocusIndex, setCurrentFocusIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const currentQuestionIndex =
    allTargetQuestions?.findIndex((item) => item.id === currentQuestion.id) ?? 0;
  const currentModuleName = currentModule?.getFormattedModuleName();
  const handleListItemClick = (question: Question) => {
    handleNavigateAway(() => handleMoveForward(question, true, true));
  };

  // this allows the scrolling to work within the button container when tabbing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        // Use setTimeout to delay the update, otherwise it accesses activeElement before its been updated
        setTimeout(() => {
          const dataIndex = (document.activeElement as HTMLElement)?.getAttribute(
            "data-index"
          );
          const currentIndex = dataIndex ? Number(dataIndex) : null;
          setCurrentFocusIndex(currentIndex);
        }, 200); // Delay of 0ms
      }
    };

    // reset the focus index when container loses focus
    const handleBlur = (e: FocusEvent) => {
      if (!containerRef.current?.contains(e.relatedTarget as Node)) {
        setCurrentFocusIndex(null);
      }
    };

    const containerElement = containerRef.current;
    if (containerElement) {
      containerElement.addEventListener("blur", handleBlur, true);
    }
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      // Cleanup listeners on component unmount
      window.removeEventListener("keydown", handleKeyDown);
      if (containerElement) {
        containerElement.removeEventListener("blur", handleBlur, true);
      }
    };
  }, []);

  const maxWidth = showGppButton ? 212 : 270;
  const targetQuestions = allTargetQuestions.filter(
    (item) => item.type !== QuestionType.guided
  );
  const buttonCount = targetQuestions.length;

  const getButtonContainerOffset = () => {
    if (!targetQuestions) return 0;

    const qIndex = currentFocusIndex ?? currentQuestionIndex ?? 0;

    const buttonWidth = 38;
    const gap = 20;

    const visibleButtonCount = Math.floor(maxWidth / (buttonWidth + gap));
    const maxVisibleOffset =
      (targetQuestions.length - 1 - visibleButtonCount) * (buttonWidth + gap);
    const centerOffset = Math.floor(visibleButtonCount / 2) * (buttonWidth + gap);
    const currentOffset = qIndex * (buttonWidth + gap);

    const offset = Math.min(
      0,
      Math.max(-maxVisibleOffset, Math.min(centerOffset - currentOffset, centerOffset))
    );
    return offset;
  };

  return (
    <>
      <Helmet>
        <title>{currentQuestion?.parent?.name!} | Digit</title>
      </Helmet>
      <Box
        ref={anchorRef}
        sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <Box
          ref={containerRef}
          sx={{
            width: `${buttonCount * 58}px`,
            maxWidth: `${maxWidth}px`,
            height: "38px",
            overflowX: "hidden",
            overflowY: "hidden",
            position: "relative",
          }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: `${getButtonContainerOffset()}px`,
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              gap: "20px",
              transition: "left 0.4s linear",
            }}>
            {targetQuestions?.map((question, i, { length }) => {
              const isSelected = currentQuestion?.id === question.id;

              return (
                <Box component="span" key={question.id}>
                  <QuestionButton
                    question={question}
                    selected={isSelected}
                    index={i}
                    {...props}
                    handleButtonClick={() => {
                      // TODO need to change the data layer text
                      let text =
                        question.type === QuestionType.mastery
                          ? "mastery problem"
                          : question.type === QuestionType.guided
                          ? "guided practice button"
                          : `practice problem ${i + 1}`;
                      // data layer team wants these top bar buttons to throw a different GTM text if the option has try again available
                      // eg, submitted, not perfect, and not in a retried state
                      if (
                        !question.is_question_perfect &&
                        !question.is_question_retried &&
                        question.is_question_submitted
                      ) {
                        text = `try again - ${text}`;
                      }

                      generateGtmEvent({
                        name: "onclick",
                        event: "link",
                        action: "click",
                        type: "internal link",
                        region: "navbar",
                        section: "main navbar",
                        text: text,
                        component: currentModuleName ?? "",
                      });
                      handleListItemClick(question);
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
        {targetQuestions?.length > 4 && (
          <WrapperQuestionsDropdown
            onSelect={handleListItemClick}
            anchorRef={anchorRef.current}
            selectedIndex={currentQuestionIndex}
            targetQuestions={allTargetQuestions}
          />
        )}
      </Box>
    </>
  );
};

export const QuestionButton = (
  props: {
    question?: Question;
    selected: boolean;
    index: number;
    handleButtonClick: (text?: string) => void;
  } & Pick<
    Lti1p3QuestionSelectorProps,
    | "showSkillVideos"
    | "isSolverLoaded"
    | "questionFeedback"
    | "userCtxRef"
    | "userCtx"
    | "metadata"
    | "handleHideRemediationReminder"
    | "remediationReminderDot"
    | "handleOpenSkillModal"
    | "remediationReminderDot"
    | "currentModule"
  >
) => {
  const {
    question,
    showSkillVideos,
    selected,
    index,
    isSolverLoaded,
    questionFeedback,
    userCtxRef,
    userCtx,
    metadata,
    currentModule,
  } = props;
  const classes = useStyles();
  const [gpMenuOpen, setGpMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [gpHover, setGpHover] = useState(false);
  const [relatedVideoHover, setRelatedVideoHover] = useState(false);

  const iconStyle = { width: "18px", height: "18px" };
  let buttonColor: ColorOptions = "neutral";
  if (selected) {
    buttonColor = "primary";
  }
  let questionNumber = question?.getQuestionNumber();
  let hoverContent = (
    <Typography variant="h4" color="inherit" className={classes.buttonText}>
      {questionNumber}
    </Typography>
  );
  let defaultContent = (
    <Typography variant="h4" color="inherit" className={classes.buttonText}>
      {questionNumber}
    </Typography>
  );
  let tooltipLabel = `Practice problem ${questionNumber}`;
  const isMastery = question?.type === QuestionType.mastery;
  const isGuided = question?.type === QuestionType.guided || !question;
  const isCompleted =
    question?.is_question_submitted && !userCtx.tryAgainSubmissions?.has(question?.id);
  // const isPerfect = perfectScores?.has(question.id);
  const isPerfect = question && userCtxRef.current.checkPerfectScore(question?.id);
  const currentModuleName = currentModule?.getFormattedModuleName();

  if (isMastery) {
    hoverContent = <IconMedalFill style={iconStyle} />;
    defaultContent = <IconMedal style={iconStyle} />;
    tooltipLabel = "Mastery problem";
  }

  if (isGuided) {
    hoverContent = <IconGridShapesFill style={iconStyle} />;
    defaultContent = <IconGridShapes style={iconStyle} />;
    tooltipLabel = "Study resources";
  }

  if ((selected && questionFeedback) || !selected) {
    if (isCompleted && !isPerfect) {
      buttonColor = "warning";
      // hoverContent = <IconRepeat style={iconStyle} />;
      defaultContent = <IconRepeat style={iconStyle} />;
      tooltipLabel = "Try again";
    } else if (isCompleted && isPerfect) {
      buttonColor = "success";
      tooltipLabel = "Correct";
      if (!isMastery) {
        hoverContent = <IconCheck style={iconStyle} />;
        defaultContent = <IconCheck style={iconStyle} />;
      }
    }
  }

  const handleClick = (e: any) => {
    if (isGuided) {
      generateGtmEvent({
        event: "collapse",
        action: "open",
        name: "onclick",
        type: "click",
        region: "navbar",
        section: "main navbar",
        text: "study resources",
        component: currentModuleName ?? "",
      });
      setGpMenuOpen(true);
      props.handleHideRemediationReminder();
      // HandleDataLayer("collapse", "open", "navbar", "main navbar", "profile dropdown");
      setAnchorEl(e.currentTarget);
    } else {
      props.handleButtonClick();
    }
  };
  const handleClose = () => {
    generateGtmEvent({
      event: "collapse",
      action: "close",
      name: "onclick",
      type: "click",
      region: "navbar",
      section: "main navbar",
      text: "study resources",
      component: currentModuleName ?? "",
    });
    setGpMenuOpen(false);
    // HandleDataLayer("collapse", "close", "navbar", "main navbar", "profile dropdown");
    setAnchorEl(null);
  };

  const Button = selected ? (
    <IconButtonContained
      onClick={handleClick}
      className={classes.iconButton}
      data-index={index}
      zoom={true}
      hover={hoverContent}
      color={buttonColor}
      tooltipLabel={tooltipLabel}
      tooltipPlacement="bottom">
      {defaultContent}
    </IconButtonContained>
  ) : (
    <IconButtonOutlined
      onClick={handleClick}
      className={classes.iconButton}
      data-index={index}
      disabled={isSolverLoaded}
      zoom={true}
      hover={hoverContent}
      color={buttonColor}
      tooltipLabel={tooltipLabel}
      tooltipPlacement="bottom">
      {defaultContent}
    </IconButtonOutlined>
  );

  const currentTargetSkills = ["test"];

  return (
    <>
      {isGuided ? (
        <Badge
          color="secondary"
          variant="dot"
          className={classes.remediationBadge}
          invisible={
            !props.remediationReminderDot ||
            !currentTargetSkills ||
            currentTargetSkills?.length < 1
          }>
          {Button}
          <Menu
            open={gpMenuOpen}
            anchorEl={anchorEl}
            onClose={handleClose}
            classes={{
              root: classes.menuWrapper,
              paper: classes.menuPaper,
              list: classes.menuList,
            }}
            PaperProps={{
              elevation: 0,
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}>
            {question &&
              !metadata?.learning_experience?.disable_guidedpractice_button &&
              (question.is_static_gpp ? (
                [
                  <MenuItem
                    key="guided-practice-video"
                    className={classes.menuItem}
                    onClick={() => {
                      userCtx.setGppTextMode(false);
                      props.handleButtonClick("guided practice video");
                      handleClose();
                    }}
                    style={{}}
                    onMouseEnter={() => setGpHover(true)}
                    onMouseLeave={() => setGpHover(false)}>
                    <i
                      className={`${gpHover ? "fa-solid" : "fa-regular"} fa-play ${
                        classes.menuItemIcon
                      }`}></i>
                    Guided practice video
                  </MenuItem>,
                  <MenuItem
                    key="guided-practice-text"
                    className={classes.menuItem}
                    onClick={() => {
                      userCtx.setGppTextMode(true);
                      props.handleButtonClick("guided practice text");
                      handleClose();
                    }}
                    style={{}}
                    onMouseEnter={() => setGpHover(true)}
                    onMouseLeave={() => setGpHover(false)}>
                    <i
                      className={`${gpHover ? "fa-solid" : "fa-regular"} fa-book ${
                        classes.menuItemIcon
                      }`}></i>
                    Guided practice text
                  </MenuItem>,
                ]
              ) : (
                <MenuItem
                  key="guided-practice"
                  className={classes.menuItem}
                  onClick={() => {
                    props.handleButtonClick();
                    handleClose();
                  }}
                  style={{}}
                  onMouseEnter={() => setGpHover(true)}
                  onMouseLeave={() => setGpHover(false)}>
                  <i
                    className={`${gpHover ? "fa-solid" : "fa-regular"} fa-book ${
                      classes.menuItemIcon
                    }`}></i>
                  Guided practice
                </MenuItem>
              ))}
            {showSkillVideos && (
              <MenuItem
                classes={classes.menuItem}
                sx={{ position: "relative" }}
                onClick={() => {
                  handleClose();
                  props.handleOpenSkillModal();
                }}
                style={{}}
                onMouseEnter={() => setRelatedVideoHover(true)}
                onMouseLeave={() => setRelatedVideoHover(false)}>
                <Badge
                  color="secondary"
                  variant="dot"
                  className={classes.remediationBadgeMenuItem}
                  invisible={
                    !props.remediationReminderDot ||
                    !currentTargetSkills ||
                    currentTargetSkills?.length < 1
                  }>
                  <Box
                    onClick={() => {
                      generateGtmEvent({
                        event: "modal",
                        action: "open",
                        name: "onclick",
                        type: "click",
                        region: "main content",
                        section: "study resources",
                        text: "related videos",
                      });
                    }}>
                    <i
                      className={`${
                        relatedVideoHover ? "fa-solid" : "fa-regular"
                      } fa-play ${classes.menuItemIcon}`}></i>
                    Related videos
                  </Box>
                </Badge>
              </MenuItem>
            )}
          </Menu>
        </Badge>
      ) : (
        Button
      )}
    </>
  );
};
